/**
 * useFeedAPI - Custom hook for managing activity feed data (JS/JSX)
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { axiosService } from '../helpers/axios';
import { useUser } from '../helpers/axios';

const useFeedAPI = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [activities, setActivities] = useState([]);
  const [suggestions, setSuggestions] = useState({ sent: [], received: [] });
  const [userStats, setUserStats] = useState({
    commentsCount: 0,
    suggestionsCount: 0,
    collaborationsCount: 0,
    likedCallsCount: 0,
  });

  const { user: currentUser } = useUser();
  const isAuthenticated = Boolean(currentUser);

  // Centralize your endpoints (adjust if your API differs)
  const endpoints = useMemo(() => ({
    comments:      '/interact/comments/',
    suggestions:   '/interact/suggestions/',
    posts:         '/interact/posts/',
    follows:       '/interact/follows/',
    calls:         '/funding/calls/',
    users:         '/users/',
    consortiums:   '/consortium/consortiums/',
    organizations: '/consortium/organizations/',
    fundingAgencies: '/funding/funding-agencies/',
    fundingPrograms: '/funding/funding-programs/'
  }), []);

  
  // suggestions to follow come from the server (/interact/follows/discover/): every page that used this hook loaded
  // all users, consortia, organisations, calls, agencies and programs here to build them in the browser

  // Computed state for suggestions loading
  const suggestionsLoading = false;
  const suggestionsError = null;



  const fetchActivityFeed = useCallback(async (userId = null) => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      const targetUserId = userId ?? currentUser?.id;

      const [commentsRes, sentSugRes, recvSugRes] = await Promise.all([
        axiosService.get(`${endpoints.comments}?author=${targetUserId}`),
        axiosService.get(`${endpoints.suggestions}by-author/`),
        axiosService.get(`${endpoints.suggestions}received/`),
      ]);

      const _comments = (commentsRes.data?.results ?? commentsRes.data ?? []).map((c) => ({
        ...c,
        type: 'comment',
        timestamp: c.updated || c.created,
        title: `Commented on "${c.call?.title || 'a call'}"`,
        description: c.body,
        typeOfInterest: c.type_of_interest,
      }));

      const sent = (sentSugRes.data?.results ?? sentSugRes.data ?? []).map((s) => ({
        ...s,
        type: 'suggestion_sent',
        timestamp: s.updated || s.created,
        title: `Suggested collaboration to ${s.receiver?.first_name ?? ''} ${s.receiver?.last_name ?? ''}`.trim(),
        description: s.body,
        call: s.call,
      }));

      const received = (recvSugRes.data?.results ?? recvSugRes.data ?? []).map((s) => ({
        ...s,
        type: 'suggestion_received',
        timestamp: s.updated || s.created,
        title: `Received collaboration suggestion from ${s.sender?.first_name ?? ''} ${s.sender?.last_name ?? ''}`.trim(),
        description: s.body,
        call: s.call,
      }));

  
// ...comments, 
      const combined = [...sent, ...received].sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
      );

      setActivities(combined);
      setUserStats({
        // commentsCount: comments.length,
        suggestionsCount: sent.length + received.length,
        collaborationsCount: received.length,
        likedCallsCount: currentUser?.calls_liked?.length || 0,
      });
    } catch (err) {
      console.error('Error fetching activity feed:', err);
      setError(err?.response?.data?.message || 'Failed to load activity feed');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, currentUser, endpoints]);

  const fetchSuggestions = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const [sentRes, recvRes] = await Promise.all([
        axiosService.get(`${endpoints.suggestions}by-author/`),
        axiosService.get(`${endpoints.suggestions}received/`),
      ]);
      setSuggestions({
        sent: sentRes.data?.results ?? sentRes.data ?? [],
        received: recvRes.data?.results ?? recvRes.data ?? [],
      });
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError(err?.response?.data?.message || 'Failed to load suggestions');
    }
  }, [isAuthenticated, endpoints]);

  const createComment = useCallback(async ({ callId, body, typeOfInterest = 0 }) => {
    if (!isAuthenticated) return null;
    try {
      setLoading(true);
      const res = await axiosService.post(endpoints.comments, {
        target_type: 'call',
        target_id: callId,
        body,
        type_of_interest: typeOfInterest,
      });
      return res.data;
    } catch (err) {
      console.error('Error creating comment:', err);
      setError(err?.response?.data?.message || 'Failed to create comment');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, endpoints]);

  const createPost = useCallback(async ({ title = '', body, visibility = 'otable', is_announcement = false, link_preview = null }) => {
    if (!isAuthenticated) return null;
    try {
      setLoading(true);
      const res = await axiosService.post(endpoints.posts, {
        title,
        body,
        visibility,
        is_announcement,
        ...(link_preview ? { link_preview } : {}),
      });
      return res.data;
    } catch (err) {
      console.error('Error creating post:', err);
      setError(err?.response?.data?.message || 'Failed to create post');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, endpoints]);

  const sendSuggestion = useCallback(async ({ callId, receiverId, body }) => {
    if (!isAuthenticated) return null;
    try {
      setLoading(true);
      const res = await axiosService.post(endpoints.suggestions, {
        call: callId,
        receiver: receiverId,
        body,
      });
      await fetchSuggestions();
      return res.data;
    } catch (err) {
      console.error('Error sending suggestion:', err);
      setError(err?.response?.data?.message || 'Failed to send suggestion');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, endpoints, fetchSuggestions]);

  const deleteComment = useCallback(async (commentId) => {
    if (!isAuthenticated) return;
    try {
      await axiosService.delete(`${endpoints.comments}${commentId}/`);
      setActivities((prev) => prev.filter((a) => !(a.type === 'comment' && a.id === commentId) && a.id !== commentId));
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError(err?.response?.data?.message || 'Failed to delete comment');
      throw err;
    }
  }, [isAuthenticated, endpoints]);

  const toggleCallLike = useCallback(async (callId) => {
    if (!isAuthenticated) return;
    try {
      const res = await axiosService.post(`${endpoints.calls}${callId}/like/`);
      setUserStats((prev) => ({
        ...prev,
        likedCallsCount: res.data?.liked ? prev.likedCallsCount + 1 : Math.max(prev.likedCallsCount - 1, 0),
      }));
      return res.data;
    } catch (err) {
      console.error('Error toggling call like:', err);
      setError(err?.response?.data?.message || 'Failed to update like status');
      throw err;
    }
  }, [isAuthenticated, endpoints]);

  const followEntity = useCallback(async (contentType, objectId) => {
    if (!isAuthenticated) return null;
    try {
      const res = await axiosService.post(endpoints.follows, {
        content_type: contentType,
        object_id: objectId,
      });
      // Don't trigger refresh - SWR cache invalidation handles updates
      return res.data;
    } catch (err) {
      console.error('Error following entity:', err);
      setError(err?.response?.data?.message || 'Failed to follow');
      throw err;
    }
  }, [isAuthenticated, endpoints]);

  const unfollowEntity = useCallback(async (contentType, objectId) => {
    if (!isAuthenticated) return null;
    try {
      // Use the backend's unfollow endpoint with query params
      const res = await axiosService.delete(
        `${endpoints.follows}unfollow/?content_type=${contentType}&object_id=${objectId}`
      );
      // Don't trigger refresh - SWR cache invalidation handles updates
      return res.data;
    } catch (err) {
      console.error('Error unfollowing entity:', err);
      setError(err?.response?.data?.message || 'Failed to unfollow');
      throw err;
    }
  }, [isAuthenticated, endpoints]);

  const checkFollowStatus = useCallback(async (contentType, objectId) => {
    if (!isAuthenticated) return false;
    try {
      const res = await axiosService.get(`${endpoints.follows}status/?content_type=${contentType}&object_id=${objectId}`);
      return res.data?.following || false;
    } catch (err) {
      console.error('Error checking follow status:', err);
      return false;
    }
  }, [isAuthenticated, endpoints]);

  const fetchFollowing = useCallback(async () => {
    if (!isAuthenticated) return [];
    try {
      const res = await axiosService.get(`${endpoints.follows}following/`);
      return res.data ?? [];
    } catch (err) {
      console.error('Error fetching following:', err);
      setError(err?.response?.data?.message || 'Failed to fetch following');
      return [];
    }
  }, [isAuthenticated, endpoints]);

  const fetchFollowers = useCallback(async () => {
    if (!isAuthenticated) return [];
    try {
      const res = await axiosService.get(`${endpoints.follows}followers/`);
      // console.log('????  Fetched followers:', res.data);
      return res.data ?? [];
    } catch (err) {
      console.error('Error fetching followers:', err);
      setError(err?.response?.data?.message || 'Failed to fetch followers');
      return [];
    }
  }, [isAuthenticated, endpoints]);

  // LIMIT suggestions drawn at random by the server (people you are not connected with, things you do not follow);
  // the same rows as before: {id, type, content_type, object_id, name, avatar, description}
  const fetchSuggestionsToFollow = useCallback(async (limit = 10) => {
    if (!isAuthenticated) return [];
    const seed = Math.floor(Math.random() * 1e9);
    const rows = [];
    try {
      for (let page = 0; rows.length < limit; page += 1) {
        const res = await axiosService.get('/interact/follows/discover/', { params: { seed, page } });
        // the type names the pages using this know ('agency', 'program' for the funders)
        rows.push(...(res.data?.results || []).map((r) => ({ ...r, type: { fundingagency: 'agency', fundingprogram: 'program' }[r.kind] || r.kind })));
        if (!res.data?.has_more) break;
      }
    } catch (err) {
      setError(err?.message || 'Failed to load suggestions');
    }
    return rows.slice(0, limit);
  }, [isAuthenticated]);

  const searchUsers = useCallback(async (query) => {
    if (!isAuthenticated || !query.trim()) return [];
    try {
      const res = await axiosService.get(`${endpoints.users}?search=${encodeURIComponent(query)}`);
      return res.data?.results ?? res.data ?? [];
    } catch {
      return [];
    }
  }, [isAuthenticated, endpoints]);

  const fetchConsortiumMembersPosts = useCallback(async (userId = null, limit = 15, postsPerMember = 2) => {
    if (!isAuthenticated) return [];
    try {
      const targetUserId = userId ?? currentUser?.id;
      
      // Get user's consortium memberships
      const membershipsRes = await axiosService.get(`/consortium/memberships/by-user/?user_id=${targetUserId}`);
      const memberships = membershipsRes.data?.results || membershipsRes.data || [];
      
      if (memberships.length === 0) return [];
      
      const consortiumPosts = [];
      const seenPostIds = new Set();

      // Limit the number of consortia to process; all of them at once, and each consortium's members'
      // posts in one request (not one request per member, one member after the other)
      const consortiaToProcess = memberships.slice(0, 5);
      const perConsortium = await Promise.all(consortiaToProcess.map(async (userMembership) => {
        const consortiumId = userMembership.consortium?.id;
        if (!consortiumId) return [];
        try {
          const membersRes = await axiosService.get(`/consortium/memberships/?consortium=${consortiumId}`);
          const memberIds = (membersRes.data?.results || membersRes.data || [])
            .filter(m => m?.member && m.member !== targetUserId)
            .slice(0, limit)
            .map(m => m.member);
          if (!memberIds.length) return [];
          const postsRes = await axiosService.get(`${endpoints.posts}?authors=${memberIds.join(',')}&page_size=${memberIds.length * postsPerMember}`);
          return (postsRes.data?.results || postsRes.data || []).map(post => ({ post, userMembership }));
        } catch (err) {
          console.error(`Error fetching posts of consortium ${consortiumId}:`, err);
          return [];
        }
      }));

      const perAuthor = {};
      perConsortium.flat().forEach(({ post, userMembership }) => {
        // only posts since the user joined that consortium, at most postsPerMember per member
        const author = String(post.author?.id || post.author);
        if (seenPostIds.has(post.id) || new Date(post.created) < new Date(userMembership.created)) return;
        perAuthor[author] = (perAuthor[author] || 0) + 1;
        if (perAuthor[author] > postsPerMember) return;
        seenPostIds.add(post.id);
        consortiumPosts.push({
          ...post,
          network_source: 'consortium_member',
          consortium_name: userMembership.consortium?.name
        });
      });
      
      // Sort by date
      consortiumPosts.sort((a, b) => new Date(b.created) - new Date(a.created));
      
      return consortiumPosts;
    } catch (err) {
      console.error('Error fetching consortium members posts:', err);
      return [];
    }
  }, [isAuthenticated, currentUser, endpoints]);

  const refreshFeed = useCallback(async (userId = null) => {
    if (!isAuthenticated) return;
    await fetchActivityFeed(userId);
  }, [fetchActivityFeed, isAuthenticated]);

  const feedApiResult = useMemo(() => ({
    loading,
    error,
    activities,
    suggestions,
    userStats,
    fetchActivityFeed,
    fetchSuggestions,
    createComment,
    createPost,
    sendSuggestion,
    deleteComment,
    toggleCallLike,
    followEntity,
    unfollowEntity,
    checkFollowStatus,
    fetchFollowing,
    fetchFollowers,
    fetchSuggestionsToFollow,
    fetchConsortiumMembersPosts,
    searchUsers,
    refreshFeed,
    setError,
    // Expose SWR states for follow suggestions
    suggestionsLoading,
    suggestionsError,
  }), [
    loading,
    error,
    activities,
    suggestions,
    fetchActivityFeed,
    fetchSuggestions,
    createComment,
    createPost,
    sendSuggestion,
    deleteComment,
    toggleCallLike,
    followEntity,
    unfollowEntity,
    checkFollowStatus,
    fetchFollowing,
    fetchFollowers,
    fetchSuggestionsToFollow,
    fetchConsortiumMembersPosts,
    searchUsers,
    refreshFeed,
    setError,
    suggestionsLoading,
    suggestionsError,
  ]);

  return feedApiResult;
};

export default useFeedAPI;
